import type { Category, CategoryAttribute } from '../types/category';

export type AdminAttributeRow = {
  key: string;
  label: string;
  value: string;
  secret?: boolean;
};

const SKIP_KEYS = new Set([
  'found_object_id',
  'public_transport',
  'possible_loss_locations',
]);

function formatBoolean(value: unknown): string {
  const b = value === true || value === 'true' || value === 1 || value === '1';
  return b ? 'Oui' : 'Non';
}

function formatPrimitive(value: unknown, def?: CategoryAttribute): string {
  if (value === null || value === undefined || value === '') return '—';
  if (def?.type === 'boolean') return formatBoolean(value);
  if (Array.isArray(value)) {
    return value.map((item) => formatPrimitive(item)).join(', ');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
}

function attributeLabel(key: string, def?: CategoryAttribute): string {
  if (key === 'secret_clues') return 'Détails secrets';
  if (def?.label?.trim()) return def.label.trim();
  return key.replace(/_/g, ' ');
}

function formatPublicTransport(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  const pt = value as Record<string, unknown>;
  const parts: string[] = [];
  const mode = typeof pt.transport_mode === 'string' ? pt.transport_mode : '';
  if (mode) {
    const modeLabels: Record<string, string> = {
      bus: 'Bus',
      metro: 'Métro',
      tram: 'Tramway',
      train: 'Train',
    };
    parts.push(modeLabels[mode] ?? mode);
  }
  if (typeof pt.line === 'string' && pt.line.trim()) parts.push(`Ligne ${pt.line.trim()}`);
  if (typeof pt.name === 'string' && pt.name.trim()) parts.push(pt.name.trim());
  return parts.length > 0 ? parts.join(' · ') : null;
}

function formatLossLocations(value: unknown): string | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const lines = value
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const name = typeof row.name === 'string' ? row.name.trim() : '';
      const providerName = typeof row.provider_name === 'string' ? row.provider_name.trim() : '';
      if (name && providerName) return `${name} (${providerName})`;
      return name || providerName || null;
    })
    .filter((line): line is string => Boolean(line));
  return lines.length > 0 ? lines.join('\n') : null;
}

export function buildAdminAttributeRows(
  objectAttributes: Record<string, unknown> | null | undefined,
  category?: Category | null
): AdminAttributeRow[] {
  const attrs = objectAttributes ?? {};
  const defs = category?.attributes ?? {};
  const rows: AdminAttributeRow[] = [];
  const usedKeys = new Set<string>();

  for (const [key, def] of Object.entries(defs)) {
    if (SKIP_KEYS.has(key) || def.disabled) continue;
    const value = attrs[key];
    if (value === undefined || value === null || value === '') continue;
    usedKeys.add(key);
    rows.push({
      key,
      label: attributeLabel(key, def),
      value: formatPrimitive(value, def),
      secret: key === 'secret_clues' || Boolean(def.secret_detail),
    });
  }

  for (const [key, value] of Object.entries(attrs)) {
    if (usedKeys.has(key) || SKIP_KEYS.has(key) || key.startsWith('_')) continue;
    if (value === undefined || value === null || value === '') continue;
    rows.push({
      key,
      label: attributeLabel(key, defs[key]),
      value: formatPrimitive(value, defs[key]),
      secret: key === 'secret_clues' || Boolean(defs[key]?.secret_detail),
    });
  }

  const publicTransport = formatPublicTransport(attrs.public_transport);
  if (publicTransport) {
    rows.push({
      key: 'public_transport',
      label: 'Transport en commun',
      value: publicTransport,
    });
  }

  const lossLocations = formatLossLocations(attrs.possible_loss_locations);
  if (lossLocations) {
    rows.push({
      key: 'possible_loss_locations',
      label: 'Lieux de perte possibles',
      value: lossLocations,
    });
  }

  return rows;
}
